/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query';
import {
    formatDate,
    formatGender,
    formatNameLastFirst,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { FormSectionWithRows } from '@strata/react-form-section';
import { MailToHyperlink } from '@strata/react-hyperlink';
import {
    AddressDetailLabelValue,
    DetailLabelValuePair,
    PatientInfoList,
} from '@strata/react-label-value-pairs';

import { useGetDiagnosisQuery } from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';
import { ReferralDetails } from 'store/api/treatmentApi';

interface PatientDetailsProps {
    showFullDetails: boolean;
    selectedReferral?: {
        payerId: string;
        referralId: string;
        valueCases: { strat_rule_name: string; value_case_name?: string }[];
        incentiveTotal?: number;
    };
    referralDetails?: ReferralDetails;
}

export const PatientDetails = ({
    showFullDetails,
    selectedReferral = undefined,
    referralDetails = undefined,
}: PatientDetailsProps) => {
    const { data: memberDetails, isFetching: isMemberDetailsFetching } = useGetMemberDetailsQuery(
        referralDetails?.memberId &&
            referralDetails.payerId &&
            referralDetails.memberFirstName &&
            referralDetails.memberLastName
            ? {
                  segmentId: referralDetails.segmentId,
                  memberId: referralDetails.memberId,
                  firstName: referralDetails.memberFirstName,
                  lastName: referralDetails.memberLastName,
                  dateOfBirth: referralDetails.memberDOB,
                  payerId: referralDetails.payerId,
              }
            : skipToken,
    );
    const { data: diagnosisData, isFetching: isDiagnosisFetching } = useGetDiagnosisQuery(
        referralDetails?.diagnosisOid ? { diagnosisOid: referralDetails.diagnosisOid } : skipToken,
    );

    return (
        <FormSectionWithRows
            sectionTitle="Patient Details"
            isLoading={!referralDetails || isMemberDetailsFetching || isDiagnosisFetching}
            rowOneContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Name"
                            value={formatNameLastFirst(
                                memberDetails?.lastName,
                                memberDetails?.firstName,
                            )}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Gender"
                            value={formatGender(memberDetails?.gender)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="DOB"
                            value={formatDate(memberDetails?.dateOfBirth)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Age"
                            value={getAge(memberDetails?.dateOfBirth)}
                        />
                    </>
                ) : (
                    <>
                        <AddressDetailLabelValue
                            style={{ width: '25%' }}
                            label="Location"
                            city={referralDetails?.memberCity}
                            state={referralDetails?.memberState}
                            zip={referralDetails?.memberZip}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Age"
                            value={getAge(referralDetails?.memberDOB)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Diagnosis"
                            value={diagnosisData?.description}
                        />
                        <PatientInfoList
                            style={{ width: '25%' }}
                            label="Attributes"
                            incentiveTotal={selectedReferral?.incentiveTotal}
                            values={selectedReferral?.valueCases}
                        />
                    </>
                )
            }
            rowTwoContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Diagnosis"
                            value={diagnosisData?.description}
                        />
                        <AddressDetailLabelValue
                            style={{ width: '25%' }}
                            address1={memberDetails?.memberAddress1}
                            address2={memberDetails?.memberAddress2}
                            city={memberDetails?.memberCity}
                            state={memberDetails?.memberState}
                            zip={memberDetails?.memberZip}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Phone"
                            value={formatPhoneNumber(memberDetails?.phone)}
                        />
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Email"
                            value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                        />
                    </>
                ) : undefined
            }
            rowThreeContent={
                showFullDetails ? (
                    <>
                        <DetailLabelValuePair
                            style={{ width: '25%' }}
                            label="Member ID"
                            value={memberDetails?.memberId}
                        />
                        <PatientInfoList
                            style={{ width: '25%' }}
                            label="Attributes"
                            incentiveTotal={selectedReferral?.incentiveTotal}
                            values={selectedReferral?.valueCases}
                        />
                    </>
                ) : undefined
            }
        />
    );
};
